// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricConfig, BaseMetricEntity, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for PullRequestsOpened entity
 */
type PullRequestsOpenedConfig = BaseMetricConfig;

/**
 * PullRequestsOpenedEntity - Domain entity for pull requests opened business rules
 *
 * This entity encapsulates the business logic for calculating and determining
 * pull requests opened influence levels based on percentage of PRs opened by company employees.
 *
 * Business Rules:
 * - 0.0-0.9% of PRs opened = Silent (0 points) - Red color, (!) icon
 * - 1.0-4.9% of PRs opened = Participating (1 point) - Yellow color, line in circle icon
 * - 5.0-19.9% of PRs opened = Contributing (2 points) - Blue color, thumbs up icon
 * - > 20% of PRs opened = Leading (3 points) - Green color, thumbs up icon
 */
export class PullRequestsOpened extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'pull_requests_opened' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: PullRequestsOpenedConfig = {
    metricType: 'pull_requests_opened',
    display: {
      displayName: 'Pull Requests Opened',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of PRs opened are from our company?',
        scoringRules: [
          'Each 0.1% of PRs to the project total = 0.1 point, and any number above 0 gets a +1 bonus',
          '0 PRs = 0 points',
          '0.000001 - 0.1% of PRs = 1.1 points',
          '0.2% of PRs = 1.2 points',
          '2.5% of PRs = 3.5 points',
          '20.0% of PRs = 21.0 points',
          '0 points = <b>Silent</b>',
          '1 point = <b>Participating</b>',
          '2-9 points = <b>Contributing</b>',
          '10+ points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: 'Our company has not opened any PRs for this project.',
      },
      positiveValue: {
        descriptionTemplate: 'Employees from our company opened {percentage}% of all PRs for this project.',
      }
    }
  };

  static getMetricConfig() {
    return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for pull requests opened
   */
  static getBottomSummary(value?: string | number, level?: ScoreLevel): MetricBottomSummary {
    const prPercentage = typeof value === 'string' ? parseFloat(value) : (value || 0);
    const styles = level ? this.getStylesByLevel(level) : this.getNullValueStyles();
    const bottomSummary = this.CONFIG.bottomSummary;

    if (prPercentage === 0) {
      return {
        title: this.CONFIG.display.displayName,
        description: bottomSummary.zeroValue.description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: prPercentage.toString()
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }
}
